/**
 * PATH: apps/qa/src/pages/EvidenceUploadPage.tsx
 * PURPOSE: Upload evidence (photos, documents, videos) to inspections
 * 
 * WHY: Users need to capture and upload evidence during inspections.
 * Uses two-step upload: Files API for binary → QA API for metadata.
 * 
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 *   │ Select      │───▶│ Upload to    │───▶│ Create QA   │
 *   │ Files       │    │ Files API    │    │ Evidence    │
 *   └─────────────┘    └──────────────┘    └─────────────┘
 * 
 * DEPENDENCIES:
 *   - @quotech/api: API client
 *   - Files API: /api/files/upload/
 */

import { useState, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { normalizeList } from '@quotech/job-hub/jobs/qa'
import api from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  ArrowLeft,
  Upload,
  Camera,
  FileText,
  Video,
  X,
  CheckCircle,
  Loader2,
  AlertTriangle,
  Image,
  Plus,
} from 'lucide-react'

interface UploadFile {
  id: string
  file: File
  preview?: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  fileId?: string  // ID from Files API after upload
  error?: string
}

export default function EvidenceUploadPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [files, setFiles] = useState<UploadFile[]>([])
  const [inspectionId, setInspectionId] = useState('')
  const [areaId, setAreaId] = useState('')
  const [caption, setCaption] = useState('')
  const [evidenceType, setEvidenceType] = useState<'photo' | 'document' | 'video'>('photo')

  // Fetch inspections for selection
  const { data: inspectionsData } = useQuery({
    queryKey: ['qa-inspections-list'],
    queryFn: async () => {
      const response = await api().get('/qa/inspections/', { params: { page_size: 100 } })
      return response.data
    },
  })

  // Fetch areas for selected inspection. `qa-areas` is shared with the
  // job-hub QA tab, which reads it as a plain array — normalize in the
  // queryFn so one key can only ever hold one shape.
  const { data: areas = [] } = useQuery({
    queryKey: ['qa-areas', inspectionId],
    queryFn: async () => {
      const response = await api().get('/qa/areas/', { params: { inspection_id: inspectionId } })
      return normalizeList<{ id: string; area_name: string; floor_name?: string }>(response.data)
    },
    enabled: !!inspectionId,
  })

  const inspections = inspectionsData?.results || []

  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (!selectedFiles) return

    const newFiles: UploadFile[] = Array.from(selectedFiles).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      status: 'pending',
      progress: 0,
    }))

    setFiles(prev => [...prev, ...newFiles])
  }, [])

  // Handle drag and drop
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const droppedFiles = event.dataTransfer.files
    if (!droppedFiles) return

    const newFiles: UploadFile[] = Array.from(droppedFiles).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      status: 'pending',
      progress: 0,
    }))

    setFiles(prev => [...prev, ...newFiles])
  }, [])

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  // Remove file
  const removeFile = (id: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id)
      if (file?.preview) URL.revokeObjectURL(file.preview)
      return prev.filter(f => f.id !== id)
    })
  }

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!inspectionId) throw new Error('Please select an inspection')
      if (!areaId) throw new Error('Please select an area')
      if (files.length === 0) throw new Error('Please select at least one file')

      // Upload each file
      for (const uploadFile of files) {
        if (uploadFile.status !== 'pending') continue

        setFiles(prev => prev.map(f => 
          f.id === uploadFile.id ? { ...f, status: 'uploading' } : f
        ))

        try {
          // Step 1: Upload to Files API
          const formData = new FormData()
          formData.append('file', uploadFile.file)
          formData.append('purpose', 'qa_evidence')

          // NOTE: @quotech/api baseURL already includes `/api`
          const uploadResponse = await api().post('/files/upload/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const progress = progressEvent.total 
                ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                : 0
              setFiles(prev => prev.map(f => 
                f.id === uploadFile.id ? { ...f, progress } : f
              ))
            },
          })

          const fileId = uploadResponse.data.id

          // Step 2: Create evidence record in QA
          await api().post('/qa/evidence/', {
            // NOTE: Evidence is linked via Area → Floor → Inspection
            area: areaId,
            type: evidenceType,
            description: caption || uploadFile.file.name,
            asset_id: fileId,
          })

          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id ? { ...f, status: 'success', fileId } : f
          ))
        } catch (err) {
          setFiles(prev => prev.map(f => 
            f.id === uploadFile.id ? { 
              ...f, 
              status: 'error', 
              error: err instanceof Error ? err.message : 'Upload failed' 
            } : f
          ))
        }
      }

      // Invalidate evidence cache
      queryClient.invalidateQueries({ queryKey: ['qa-evidence'] })
    },
  })

  const pendingCount = files.filter(f => f.status === 'pending').length
  const successCount = files.filter(f => f.status === 'success').length
  const allDone = files.length > 0 && pendingCount === 0 && !uploadMutation.isPending

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="w-6 h-6 text-blue-500" />
    if (file.type.startsWith('video/')) return <Video className="w-6 h-6 text-purple-500" />
    return <FileText className="w-6 h-6 text-amber-500" />
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/evidence')}
          className="p-2 rounded-lg hover:bg-accent transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Upload Evidence</h1>
          <p className="text-muted-foreground">
            Add photos, documents, or videos to an inspection
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-6">
        {/* Inspection selection */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Inspection <span className="text-destructive">*</span>
          </label>
          <select
            value={inspectionId}
            onChange={(e) => {
              setInspectionId(e.target.value)
              setAreaId('')
            }}
            className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
            <option value="">Select an inspection...</option>
            {inspections.map((inspection: { id: string; inspection_name: string }) => (
              <option key={inspection.id} value={inspection.id}>
                {inspection.inspection_name}
              </option>
            ))}
          </select>
        </div>

        {/* Area selection (optional) */}
        {inspectionId && areas.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Area (optional)
            </label>
            <select
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">General (no specific area)</option>
              {areas.map((area: { id: string; area_name: string; floor_name?: string }) => (
                <option key={area.id} value={area.id}>
                  {area.floor_name && `${area.floor_name} › `}{area.area_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Evidence type */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Evidence Type
          </label>
          <div className="flex gap-2">
            {[
              { value: 'photo', label: 'Photo', icon: Camera },
              { value: 'document', label: 'Document', icon: FileText },
              { value: 'video', label: 'Video', icon: Video },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setEvidenceType(value as typeof evidenceType)}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border font-medium transition-colors',
                  evidenceType === value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-input text-muted-foreground hover:bg-accent'
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Caption */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Caption (optional)
          </label>
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Describe the evidence..."
            className="w-full px-4 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={cn(
            'border-2 border-dashed rounded-xl p-8 text-center transition-colors',
            'border-border hover:border-primary/50 hover:bg-primary/5'
          )}
        >
          <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-foreground font-medium">
            Drop files here or click to browse
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Supports images, PDFs, and videos up to 50MB
          </p>
          <input
            type="file"
            multiple
            accept="image/*,video/*,application/pdf,.doc,.docx"
            onChange={handleFileSelect}
            className="absolute inset-0 opacity-0 cursor-pointer"
            style={{ position: 'relative', marginTop: '1rem' }}
          />
          <label className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            <Plus className="w-4 h-4" />
            Select Files
            <input
              type="file"
              multiple
              accept="image/*,video/*,application/pdf,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">
              {files.length} file{files.length !== 1 ? 's' : ''} selected
            </p>
            {files.map((file) => (
              <div
                key={file.id}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border',
                  file.status === 'success' && 'border-emerald-200 bg-emerald-50 dark:border-emerald-800/50 dark:bg-emerald-900/20',
                  file.status === 'error' && 'border-destructive/30 bg-destructive/10',
                  file.status === 'pending' && 'border-border bg-muted/50',
                  file.status === 'uploading' && 'border-primary/30 bg-primary/5'
                )}
              >
                {/* Preview */}
                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                  {file.preview ? (
                    <img src={file.preview} alt="" className="w-full h-full object-cover" />
                  ) : (
                    getFileIcon(file.file)
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground text-sm truncate">
                    {file.file.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {(file.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  {file.status === 'uploading' && (
                    <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}
                  {file.error && (
                    <p className="text-xs text-destructive mt-1">{file.error}</p>
                  )}
                </div>

                {/* Status/Actions */}
                <div className="flex-shrink-0">
                  {file.status === 'pending' && (
                    <button
                      onClick={() => removeFile(file.id)}
                      className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-destructive"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                  {file.status === 'uploading' && (
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                  )}
                  {file.status === 'success' && (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  )}
                  {file.status === 'error' && (
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Link
            to="/evidence"
            className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors"
          >
            Cancel
          </Link>
          {allDone ? (
            <Link
              to="/evidence"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Done ({successCount} uploaded)
            </Link>
          ) : (
            <button
              onClick={() => uploadMutation.mutate()}
              disabled={!inspectionId || !areaId || files.length === 0 || uploadMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload {pendingCount > 0 ? `(${pendingCount})` : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

